import axios from 'axios';
import { isGrpcRuntimeAvailable } from '../api/grpc/grpcRuntime';
import { pipeexRuntimeConfig } from '../config/pipeexRuntimeConfig';
import { DEVICE_VERIFICATION_ERROR, INVALID_FINGERPRINT_ERROR } from '../lib/machineFingerprint';
import { invalidateFingerprint, isCurrentIdentity, requireRuntimeIdentity, type VerifiedIdentity } from './authStoreFingerprint';
import { decodeJwt, persistSession } from './authStoreSession';
import type { AuthActions, AuthGet, AuthSet, AuthUser, RegistrationParams } from './authStoreTypes';

const API_BASE_URL = pipeexRuntimeConfig.apiBaseUrl;

type TokenResponse = { accessToken: string; refreshToken: string };

function completeLogin(set: AuthSet, accessToken: string, refreshToken: string): boolean {
  const user = decodeJwt(accessToken);
  if (!user) {
    set({ loading: false, error: 'Failed to decode access token' });
    return false;
  }

  persistSession(accessToken, refreshToken, user);
  set({ accessToken, refreshToken, user, loading: false });
  return true;
}

function completeMockLogin(set: AuthSet, accessToken: string, refreshToken: string, user: AuthUser): boolean {
  persistSession(accessToken, refreshToken, user);
  set({ accessToken, refreshToken, user, loading: false });
  return true;
}

async function loginWithMockMode(set: AuthSet, username: string, password: string): Promise<boolean> {
  if (username === 'test' && password === 'test') {
    return completeMockLogin(
      set,
      'mock-access-token',
      'mock-refresh-token',
      { id: 'mock-user', username: 'test', role: 'Mock' }
    );
  }

  if (username === 'admin' && password === 'loginadmin') {
    return completeMockLogin(
      set,
      'mock-admin-token',
      'mock-admin-refresh',
      { id: 'mock-admin', username: 'admin', role: 'Super' }
    );
  }

  try {
    const response = await axios.post<TokenResponse>(`${API_BASE_URL}/auth/login`, { username, password });
    if (isGrpcRuntimeAvailable()) {
      set({ loading: false, error: DEVICE_VERIFICATION_ERROR });
      return false;
    }

    const { accessToken, refreshToken } = response.data || {};
    if (accessToken && refreshToken && completeLogin(set, accessToken, refreshToken)) return true;
  } catch {
    // Real API unreachable — fall through to mock error.
  }

  set({ loading: false, error: 'Invalid credentials for mock mode (use test / test)' });
  return false;
}

async function loginWithWebApi(set: AuthSet, username: string, password: string): Promise<boolean> {
  const response = await axios.post<TokenResponse>(`${API_BASE_URL}/auth/login`, { username, password });
  if (isGrpcRuntimeAvailable()) {
    set({ loading: false, error: DEVICE_VERIFICATION_ERROR });
    return false;
  }

  const { accessToken, refreshToken } = response.data || {};
  if (!accessToken || !refreshToken) {
    set({ loading: false, error: 'Invalid auth response from server' });
    return false;
  }

  return completeLogin(set, accessToken, refreshToken);
}

async function loginWithRuntime(
  set: AuthSet,
  get: AuthGet,
  identity: VerifiedIdentity,
  username: string,
  password: string
): Promise<boolean> {
  const response = await axios.post<TokenResponse>(`${API_BASE_URL}/auth/login-pcf`, {
    username,
    password,
    machineFingerprint: identity.fingerprint,
  });
  if (!isCurrentIdentity(get, identity)) {
    set({ loading: false, error: get().fingerprintError || DEVICE_VERIFICATION_ERROR });
    return false;
  }

  const { accessToken, refreshToken } = response.data || {};
  if (!accessToken || !refreshToken) {
    set({ loading: false, error: 'Invalid auth response from server' });
    return false;
  }

  return completeLogin(set, accessToken, refreshToken);
}

function messageFor(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') return fallback;

  const response = 'response' in error ? error.response : null;
  const data = response && typeof response === 'object' && 'data' in response ? response.data : null;
  if (data && typeof data === 'object') {
    if ('message' in data && typeof data.message === 'string' && data.message) return data.message;
    if ('error' in data && typeof data.error === 'string' && data.error) return data.error;
  }
  if (typeof data === 'string' && data) return data;
  return 'message' in error && typeof error.message === 'string' && error.message ? error.message : fallback;
}

function failRequest(set: AuthSet, message: string): false {
  if (message === INVALID_FINGERPRINT_ERROR) {
    invalidateFingerprint();
    set({ machineFingerprint: null, fingerprintLoading: false, fingerprintError: message });
  }
  set({ loading: false, error: message });
  return false;
}

export function createLoginActions(set: AuthSet, get: AuthGet): Pick<AuthActions, 'login' | 'register'> {
  return {
    login: async (username, password) => {
      set({ loading: true, error: null });
      let identity: VerifiedIdentity | null = null;

      try {
        const runtimeBridgeAvailable = isGrpcRuntimeAvailable();
        if (pipeexRuntimeConfig.useMockData && !runtimeBridgeAvailable) {
          return await loginWithMockMode(set, username, password);
        }
        if (!runtimeBridgeAvailable) return await loginWithWebApi(set, username, password);

        identity = await requireRuntimeIdentity(get);
        if (!identity || !isCurrentIdentity(get, identity)) {
          set({ loading: false, error: get().fingerprintError || DEVICE_VERIFICATION_ERROR });
          return false;
        }
        return await loginWithRuntime(set, get, identity, username, password);
      } catch (error: unknown) {
        if (identity && !isCurrentIdentity(get, identity)) {
          set({ loading: false, error: get().fingerprintError || DEVICE_VERIFICATION_ERROR });
          return false;
        }
        return failRequest(set, messageFor(error, 'Login failed'));
      }
    },

    register: async (params: RegistrationParams) => {
      set({ loading: true, error: null });
      let identity: VerifiedIdentity | null = null;

      try {
        const runtimeBridgeAvailable = isGrpcRuntimeAvailable();
        if (pipeexRuntimeConfig.useMockData && !runtimeBridgeAvailable) {
          set({ loading: false });
          return get().login(params.username, params.password);
        }
        if (!runtimeBridgeAvailable) {
          set({ loading: false, error: 'Runtime bridge is unavailable and mock mode is disabled' });
          return false;
        }

        identity = await requireRuntimeIdentity(get);
        if (!identity || !isCurrentIdentity(get, identity)) {
          set({ loading: false, error: get().fingerprintError || DEVICE_VERIFICATION_ERROR });
          return false;
        }
        await axios.post(`${API_BASE_URL}/users/register`, {
          userName: params.username,
          password: params.password,
          email: params.email,
          activationKey: params.activationKey,
          machineFingerPrint: identity.fingerprint,
        }, { timeout: 10000 });
        if (!isCurrentIdentity(get, identity)) {
          set({ loading: false, error: get().fingerprintError || DEVICE_VERIFICATION_ERROR });
          return false;
        }

        set({ loading: false });
        return get().login(params.username, params.password);
      } catch (error: unknown) {
        if (identity && !isCurrentIdentity(get, identity)) {
          set({ loading: false, error: get().fingerprintError || DEVICE_VERIFICATION_ERROR });
          return false;
        }
        return failRequest(set, messageFor(error, 'Registration failed'));
      }
    },
  };
}
